import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { permissionsService } from '../services';

const PermissionsContext = createContext();

/**
 * Hook to use permissions context
 */
export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

/**
 * Permissions Provider Component
 * Manages user permissions based on their subscription
 */
export const PermissionsProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  const [permissions, setPermissions] = useState({});
  const [limits, setLimits] = useState({});
  const [pages, setPages] = useState([]);
  const [actions, setActions] = useState([]);
  const [plan, setPlan] = useState(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accountSuspended, setAccountSuspended] = useState(false);

  /**
   * Fetch user permissions from server
   */
  const fetchPermissions = useCallback(async () => {
    if (!isAuthenticated) {
      setPermissions({});
      setLimits({});
      setPages([]);
      setActions([]);
      setPlan(null);
      setHasActiveSubscription(false);
      setAccountSuspended(false);
      setLoading(false);
      return;
    }

    // Don't fetch if account is already known to be suspended
    if (accountSuspended) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await permissionsService.getMyPermissions();
      
      if (response.success) {
        const data = response.data;
        setPermissions(data.permissions || {});
        setLimits(data.limits || {});
        setPages(data.pages || []);
        setActions(data.actions || []);
        setPlan(data.plan || null);
        setHasActiveSubscription(data.hasActiveSubscription || false);
        setAccountSuspended(false);
      } else {
        setError(response.message);
        // Check if account is suspended - ONLY check errorCode, not message content
        if (response.errorCode === 'ACCOUNT_SUSPENDED') {
          setAccountSuspended(true);
        } else {
          // مهم: إذا لم يكن الخطأ ACCOUNT_SUSPENDED، تأكد من أن accountSuspended = false
          setAccountSuspended(false);
        }
      }
    } catch (err) {
      console.error('Error fetching permissions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, accountSuspended]);

  // Fetch permissions only when authentication changes (not when user object changes)
  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  /**
   * Check if user can access a specific page
   * @param {string} pageCode - Page feature code (e.g., 'PAGE_OFFERS', 'PAGE_STATISTICS')
   * @returns {boolean}
   */
  const canAccessPage = useCallback((pageCode) => {
    // Normalize code to uppercase
    const normalizedCode = pageCode?.toUpperCase();
    
    // صفحات متاحة للجميع دائماً (حتى بدون اشتراك)
    const alwaysPublicPages = ['PAGE_DASHBOARD', 'PAGE_SETTINGS', 'PAGE_SUBSCRIPTION', 'PAGE_FAQ', 'PAGE_TUTORIALS', 'PAGE_NOTIFICATIONS'];
    if (alwaysPublicPages.includes(normalizedCode)) {
      return true;
    }
    
    // إذا لم يكن هناك اشتراك نشط - لا وصول
    if (!hasActiveSubscription && !plan) {
      return false;
    }
    
    // Check dynamic permissions first (try both original and normalized)
    // null يعني مسموح (القيمة الافتراضية)، false يعني ممنوع صراحة
    if (permissions[pageCode] !== undefined) {
      return permissions[pageCode] !== false; // null or true = allowed
    }
    if (permissions[normalizedCode] !== undefined) {
      return permissions[normalizedCode] !== false; // null or true = allowed
    }
    
    // Check if page is in available pages list
    if (pages.length > 0) {
      const hasPage = pages.some(p => {
        if (typeof p === 'string') return p?.toUpperCase() === normalizedCode;
        if (p?.code) return p.code?.toUpperCase() === normalizedCode;
        return false;
      });
      return hasPage;
    }
    
    // إذا كان هناك خطة (اشتراك نشط) نعتمد عليها
    if (plan && hasActiveSubscription) {
      return true;
    }
    
    // بدون خطة = لا صلاحيات
    return false;
  }, [permissions, pages, plan, hasActiveSubscription]);

  /**
   * Check if user can perform a specific action
   * @param {string} actionCode - Action feature code (e.g., 'ACTION_CREATE_OFFER', 'ACTION_CHAT')
   * @returns {boolean}
   */
  const canPerformAction = useCallback((actionCode) => {
    // إذا لم يكن هناك اشتراك نشط - لا صلاحيات
    if (!hasActiveSubscription && !plan) {
      return false;
    }
    
    const normalizedCode = actionCode?.toUpperCase();
    
    // Check dynamic permissions - explicit false means denied
    if (permissions[actionCode] === false) {
      return false;
    }
    if (permissions[normalizedCode] === false) {
      return false;
    }
    
    // null or true = allowed (null means default/allowed)
    if (permissions[actionCode] !== undefined || permissions[normalizedCode] !== undefined) {
      return true; // القيمة موجودة وليست false = مسموح
    }
    
    // Check if action is in available actions list
    if (actions.length > 0) {
      const hasAction = actions.some(a => {
        if (typeof a === 'string') return a?.toUpperCase() === normalizedCode;
        if (a?.code) return a.code?.toUpperCase() === normalizedCode;
        return false;
      });
      if (hasAction) return true;
    }
    
    // إذا كان هناك خطة واشتراك نشط نسمح بالأكشنز
    if (plan && hasActiveSubscription) {
      return true;
    }
    
    // بدون خطة = لا صلاحيات
    return false;
  }, [permissions, actions, plan, hasActiveSubscription]);

  /**
   * Check if user has a specific button/feature visible
   * @param {string} buttonCode - Button feature code
   * @returns {boolean}
   */
  const canSeeButton = useCallback((buttonCode) => {
    // إذا لم يكن هناك اشتراك نشط - لا أزرار
    if (!hasActiveSubscription && !plan) {
      return false;
    }
    
    const normalizedCode = buttonCode?.toUpperCase();
    
    // Check for explicit false
    if (permissions[buttonCode] === false) {
      return false;
    }
    if (permissions[normalizedCode] === false) {
      return false;
    }
    
    // null or true = allowed (null means default/allowed)
    if (permissions[buttonCode] !== undefined || permissions[normalizedCode] !== undefined) {
      return true; // القيمة موجودة وليست false = مسموح
    }
    
    // إذا كان هناك خطة واشتراك نشط نسمح بالأزرار
    if (plan && hasActiveSubscription) {
      return true;
    }
    
    // بدون خطة = لا أزرار
    return false;
  }, [permissions, plan, hasActiveSubscription]);

  /**
   * Get the limit value for a specific feature
   * @param {string} limitCode - Limit feature code (e.g., 'LIMIT_OFFERS', 'LIMIT_REQUESTS')
   * @returns {number} The limit value (-1 means unlimited)
   */
  const getLimit = useCallback((limitCode) => {
    const normalizedCode = limitCode?.toUpperCase();
    
    // If still loading or no subscription, return -1 (unlimited) to be permissive
    if (!hasActiveSubscription && Object.keys(limits).length === 0) {
      return -1;
    }
    
    if (limits[limitCode] !== undefined) {
      return limits[limitCode];
    }
    if (limits[normalizedCode] !== undefined) {
      return limits[normalizedCode];
    }
    
    if (permissions[limitCode] !== undefined) {
      return permissions[limitCode];
    }
    if (permissions[normalizedCode] !== undefined) {
      return permissions[normalizedCode];
    }
    
    // Default to unlimited if not found (permissive approach)
    return -1;
  }, [limits, permissions, hasActiveSubscription]);

  /**
   * Check if user has reached the limit for a feature
   * @param {string} limitCode - The limit feature code
   * @param {number} currentCount - Current usage count
   * @returns {boolean} True if limit reached
   */
  const isLimitReached = useCallback((limitCode, currentCount) => {
    const limit = getLimit(limitCode);
    
    // -1 means unlimited
    if (limit === -1) {
      return false;
    }
    
    return currentCount >= limit;
  }, [getLimit]);

  /**
   * Get remaining quota for a limit
   * @param {string} limitCode - The limit feature code
   * @param {number} currentCount - Current usage count
   * @returns {number} Remaining quota (-1 if unlimited, 0 if exhausted)
   */
  const getRemainingQuota = useCallback((limitCode, currentCount) => {
    const limit = getLimit(limitCode);
    
    // -1 means unlimited
    if (limit === -1) {
      return -1;
    }
    
    return Math.max(0, limit - currentCount);
  }, [getLimit]);

  /**
   * Check any permission by feature code
   * @param {string} featureCode - Any feature code
   * @returns {boolean|number} Permission value
   */
  const hasPermission = useCallback((featureCode) => {
    if (!hasActiveSubscription) {
      return false;
    }
    
    const normalizedCode = featureCode?.toUpperCase();
    
    if (permissions[featureCode] !== undefined) {
      return permissions[featureCode];
    }
    if (permissions[normalizedCode] !== undefined) {
      return permissions[normalizedCode];
    }
    
    return true;
  }, [hasActiveSubscription, permissions]);

  // Memoized context value
  const value = useMemo(() => ({
    // State
    permissions,
    limits,
    pages,
    actions,
    plan,
    hasActiveSubscription,
    accountSuspended,
    loading,
    error,
    
    // Methods
    fetchPermissions,
    canAccessPage,
    canPerformAction,
    canSeeButton,
    getLimit,
    isLimitReached,
    getRemainingQuota,
    hasPermission
  }), [
    permissions,
    limits,
    pages,
    actions,
    plan,
    hasActiveSubscription,
    accountSuspended,
    loading,
    error,
    fetchPermissions,
    canAccessPage,
    canPerformAction,
    canSeeButton,
    getLimit,
    isLimitReached,
    getRemainingQuota,
    hasPermission
  ]);

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export default PermissionsContext;
